import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ClipboardCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import { IMG } from "@/data/content";

const lines = ["Grow Your", "Hospitality Business.", "Get More Bookings."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { openAssessment } = useAssessment();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-20">
        <img src={IMG.heroResort} alt="Luxury resort with pool at dusk" className="h-full w-full object-cover" loading="eager" fetchpriority="high" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-gradient-to-r from-navy/90 via-navy/70 to-navy/30" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />

      <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-28 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md">
          <Sparkles className="h-4 w-4 text-sky" />
          <span className="text-xs font-medium text-white/90">Hospitality-focused digital solutions for properties that want to grow.</span>
        </motion.div>

        <h1 className="mt-8 max-w-4xl font-serif text-[2.6rem] font-bold leading-[1.05] text-white sm:text-6xl lg:text-7xl">
          {lines.map((line, i) => (
            <span key={i} className="hero-mask-line">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="text-sky">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-white/80">
          HM Services helps hotels, guest houses, resorts, villas and property owners improve their online presence, optimize booking platforms and attract more guests.
        </motion.p>

       <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, delay: 1.05 }}
  className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap"
>
  <Button
    onClick={openAssessment}
    data-testid="hero-assessment-cta"
    className="rounded-full bg-sky px-8 py-6 text-base font-semibold text-navy transition-transform duration-200 hover:scale-[1.03] hover:bg-sky-light"
  >
    <ClipboardCheck className="h-5 w-5" />
    Get a Free Property Assessment
  </Button>

  <Button
    asChild
    data-testid="hero-book-cta"
    className="rounded-full bg-white px-8 py-6 text-base font-semibold text-navy transition-transform duration-200 hover:scale-[1.03] hover:bg-slate-100"
  >
    <Link to="/book-your-stay">
      Book Your Stay
      <ArrowRight className="h-4 w-4" />
    </Link>
  </Button>

  <Button
    asChild
    variant="outline"
    data-testid="hero-list-cta"
    className="rounded-full border-white/40 bg-white/5 px-8 py-6 text-base text-white backdrop-blur-md hover:bg-white/15 hover:text-white"
  >
    <Link to="/list-your-property">
      List Your Property
      <ArrowRight className="h-4 w-4" />
    </Link>
  </Button>
</motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/50 sm:flex">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-white/60 to-transparent" />
      </div>
    </section>
  );
}
